import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { dateToDateOnlyString } from '../../../common/utils/dateUtils';
import { BookingsFilter } from '../bookingsSlice';

interface OptionItem {
  value: string;
  label: string;
}

interface SelectedFilterChipsProps {
  searchFilter: BookingsFilter;
  bookingMainTypeLabel?: string;
  externalSourceOptions: OptionItem[];
  onClearAll: () => void;
  onRemoveDateRange: (filterKey: string, filterKey2: string) => void;
  onRemoveValue: (filterKey: string) => void;
  onClearExternalBookingsOnly: () => void;
  onRemoveExternalSource: (source: string) => void;
}

export function SelectedFilterChips({
  searchFilter,
  bookingMainTypeLabel,
  externalSourceOptions,
  onClearAll,
  onRemoveDateRange,
  onRemoveValue,
  onClearExternalBookingsOnly,
  onRemoveExternalSource,
}: SelectedFilterChipsProps) {
  const { t } = useTranslation(['bookings', 'common']);

  const formatSelectedDates = (
    label: string,
    value: Date,
    value2: Date,
    filterKey: string,
    filterKey2: string,
  ) => {
    if (
      value === null ||
      value2 === null ||
      value === undefined ||
      value2 === undefined ||
      value < new Date('2020-01-01') ||
      value2 < new Date('2020-01-01')
    ) {
      return null;
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {dateToDateOnlyString(value)} - {dateToDateOnlyString(value2)}
        <Button
          variant="link"
          className="p-0"
          onClick={() => onRemoveDateRange(filterKey, filterKey2)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedValue = (
    label: string,
    value: number | string,
    filterKey: string,
  ) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {value}
        <Button
          variant="link"
          className="p-0"
          onClick={() => onRemoveValue(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  return (
    <div className="d-flex align-items-center p-24">
      <div className="d-flex flex-wrap align-items-center gap-8">
        {formatSelectedDates(
          t('selectedFilters.valid'),
          searchFilter.bookingStartDate
            ? new Date(searchFilter.bookingStartDate)
            : null,
          searchFilter.bookingEndDate
            ? new Date(searchFilter.bookingEndDate)
            : null,
          'bookingStartDate',
          'bookingEndDate',
        )}
        {formatSelectedDates(
          t('selectedFilters.created'),
          searchFilter.createdFrom ? new Date(searchFilter.createdFrom) : null,
          searchFilter.createdTo ? new Date(searchFilter.createdTo) : null,
          'createdFrom',
          'createdTo',
        )}
        {formatSelectedDates(
          t('selectedFilters.starts'),
          searchFilter.bookingStartsFrom
            ? new Date(searchFilter.bookingStartsFrom)
            : null,
          searchFilter.bookingStartsTo
            ? new Date(searchFilter.bookingStartsTo)
            : null,
          'bookingStartsFrom',
          'bookingStartsTo',
        )}
        {formatSelectedValue(
          t('selectedFilters.startsWithinDays'),
          searchFilter.bookingStartsWithinDays,
          'bookingStartsWithinDays',
        )}
        {formatSelectedValue(
          t('selectedFilters.bookingMainType'),
          bookingMainTypeLabel,
          'bookingMainType',
        )}
        {formatSelectedValue(
          t('selectedFilters.bookingAuthorName'),
          searchFilter.bookingAuthorName,
          'bookingAuthorName',
        )}
        {formatSelectedValue(
          t('selectedFilters.bookingCancellerName'),
          searchFilter.bookingCancellerName,
          'bookingCancellerName',
        )}
        {searchFilter.isCancelled !== null && (
          <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
            {t('selectedFilters.cancelled')}:{' '}
            {searchFilter.isCancelled
              ? t('common:texts.yes')
              : t('common:texts.no')}
            <Button
              variant="link"
              className="p-0"
              onClick={() => onRemoveValue('isCancelled')}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        )}
        {searchFilter.externalBookingsOnly && (
          <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
            {t('selectedFilters.externalBookingsOnly')}
            <Button
              variant="link"
              className="p-0"
              onClick={onClearExternalBookingsOnly}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        )}
        {searchFilter.externalBookingSources.length > 0 &&
          searchFilter.externalBookingSources.map(source => {
            const sourceLabel =
              externalSourceOptions.find(opt => opt.value === source)?.label ||
              source;

            return (
              <span
                key={source}
                className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
              >
                {t('selectedFilters.externalBookingSource')}: {sourceLabel}
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => onRemoveExternalSource(source)}
                >
                  <span className="bi bi-x" />
                </Button>
              </span>
            );
          })}
      </div>
      <Button
        onClick={onClearAll}
        variant="outline-blue"
        size="sm"
        className="ms-auto"
      >
        {t('common:buttons.emptySearchFilters')}
      </Button>
    </div>
  );
}
